import { useCallback, useRef, useState } from "react";

/**
 * 统一的页面导航 Hook：currentPage 状态 + 返回栈
 *
 * 背景：本应用用 currentPage 这个 state 模拟路由，没有浏览器原生返回栈，
 * 原先靠 featureFromPage / basicInfoFromPage 等「来源页变量」手工记账。
 * 单值变量会被多条跳转链路互相覆盖，且只能记住一层，容易返回错页。
 * 本 Hook 用返回栈（数组）替代来源页变量，并把「记账 + 切页」收敛为一个入口。
 *
 * 语义约定：
 * - goTo(page)                       前进：把当前页压入返回栈
 * - goTo(page, { from })             前进：显式指定要记录为来源的页面（默认取当前页）
 * - goTo(page, { stack: "replace" }) 透传：切换页面但不入栈（对话页等中间态）
 * - goTo(page, { stack: "clear" })   重置：清空返回栈后切换（登录 / 登出），目标页相同时也会清栈
 * - goBack(fallback)                 回退：弹出栈顶；栈空时使用 fallback
 * - rememberResume() / resume()      中断-恢复：登录等流程打断前记下位置，流程结束后回到该位置
 *
 * @param {string} initialPage 初始页面标识
 */
export function usePageNavigation(initialPage = "main") {
    const [currentPage, setCurrentPageState] = useState(initialPage);
    // 与 currentPage 同步的 ref，保证在事件回调 / 异步逻辑中能取到最新页面
    const pageRef = useRef(initialPage);
    // 返回栈：记录「从哪里来」，支持多层回退，且各条链路互不覆盖
    const backStackRef = useRef([]);
    // 中断-恢复：登录等流程会打断用户操作，记录中断前所在页面，流程结束后送回
    const resumeRef = useRef(initialPage);

    const goTo = useCallback((page, { stack = "push", from } = {}) => {
        if (!page) return;
        if (stack === "clear") {
            // 重置场景（登录 / 登出）：无论目标页是否与当前页相同，都必须清空返回栈
            backStackRef.current = [];
        } else if (page === pageRef.current) {
            // 目标页与当前页相同：不切换页面，也不污染返回栈
            return;
        } else if (stack === "push") {
            // 默认把当前页记为来源，可用 from 显式指定（如必须回到首页的场景）
            backStackRef.current.push(from ?? pageRef.current);
        }
        // stack === "replace"：保持返回栈不变，仅切换当前页
        pageRef.current = page;
        setCurrentPageState(page);
    }, []);

    const goBack = useCallback(
        (fallback = initialPage) => {
            const prevPage = backStackRef.current.pop() || fallback;
            pageRef.current = prevPage;
            setCurrentPageState(prevPage);
        },
        [initialPage],
    );

    // 登录 / 会员等中断流程触发前调用，记录中断前所在页面
    const rememberResume = useCallback(() => {
        resumeRef.current = pageRef.current;
    }, []);

    // 中断流程结束后调用，回到中断前所在页面
    const resume = useCallback(() => {
        const target = resumeRef.current || initialPage;
        pageRef.current = target;
        setCurrentPageState(target);
    }, [initialPage]);

    return { currentPage, goTo, goBack, rememberResume, resume };
}
